import { Gift, Loader2, Share2, TrendingUp, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, cardClass } from "@/components/shell/PageHeader";
import { ShareLinkButtons } from "@/components/ShareLinkButtons";
import { buildShareMessage, useMyInvite } from "@/hooks/useMyInvite";

const initialsOf = (name?: string) =>
  (name || "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "J";

const ReferralsPage = () => {
  const { user } = useAuth();
  const { invite, stats, loading } = useMyInvite();

  // What the referee actually gets is the free plan's allocation plus any signup bonus — never the bonus alone
  const refereeTotal = invite ? invite.free_plan_credits + invite.referee_bonus_credits : 0;
  const referrerBonus = invite?.referrer_bonus_credits ?? 0;

  const steps = [
    { icon: Share2, label: "Share your link", detail: "Send it to a friend, any way you like" },
    {
      icon: UserPlus,
      label: "They sign up",
      detail: refereeTotal > 0 ? `They get ${refereeTotal} credits instantly` : "They create a free account",
    },
    {
      icon: TrendingUp,
      label: "You earn credits",
      detail: referrerBonus > 0 ? `You get ${referrerBonus} credits after their first payment on a $25/month or higher plan` : "You get credit for the referral",
    },
  ];

  return (
    <div className="mx-auto w-full max-w-[1080px]">
      <PageHeader
        eyebrow="Your account"
        title="Share Joylo"
        description="Share Joylo with someone who has an idea they want to build."
      />

      <section className={`${cardClass} grid grid-cols-1 gap-8 overflow-hidden p-6 sm:p-8 lg:grid-cols-[1.4fr_1fr] lg:items-center`}>
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-plum">Your referral reward</span>
          <h2 className="mt-2.5 font-display text-[24px] font-semibold leading-tight tracking-[-0.03em] text-foreground sm:text-[28px]">
            {referrerBonus > 0 ? `You earn ${referrerBonus} credits` : "Invite a friend to Joylo"}
          </h2>
          <p className="mt-2.5 max-w-[520px] text-[14px] leading-relaxed text-muted-foreground">
            Share your link with a friend. You receive your referral reward after their first payment on a $25/month or higher plan.
          </p>
          {refereeTotal > 0 && (
            <p className="mt-3 max-w-[520px] text-[13px] leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">Your friend's signup benefit:</span>{" "}
              {refereeTotal} credits to start building. This is separate from your referral reward.
            </p>
          )}
          <div className="mt-5">
            {loading ? (
              <div className="flex min-h-[42px] items-center gap-2 text-[14px] text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading your invite link…
              </div>
            ) : invite ? (
              <ShareLinkButtons
                shareUrl={invite.share_url}
                shareMessage={buildShareMessage(refereeTotal)}
                copyToastDescription="Invite link copied to clipboard."
              />
            ) : (
              <p className="rounded-lg border border-dashed border-line bg-soft px-4 py-3 text-[13px] text-muted-foreground">
                Your invite link isn't available right now. Please try again later.
              </p>
            )}
          </div>
        </div>

        {/* Decorative: you at the centre, friends fanning out */}
        <div aria-hidden="true" className="hidden items-center justify-center gap-4 lg:flex">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-gradient-sunset font-display text-[18px] font-semibold text-white shadow-[0_8px_24px_#a23c9133]">
            {initialsOf(user?.name)}
          </span>
          <span className="h-px w-14 bg-gradient-to-r from-plum/60 to-line" />
          <div className="flex flex-col gap-2.5">
            {[Gift, UserPlus, TrendingUp].map((Icon, i) => (
              <span key={i} className="grid h-11 w-11 place-items-center rounded-full border border-line bg-selected text-plum">
                <Icon className="h-[18px] w-[18px]" />
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { value: stats?.total_invited, label: "People invited" },
          { value: stats?.total_converted, label: "Upgraded to a paid plan" },
          { value: stats?.total_credits_earned, label: "Credits earned", accent: true },
        ].map(({ value, label, accent }) => (
          <div key={label} className={`${cardClass} p-5`}>
            <strong className={`block font-display text-[28px] font-semibold tracking-[-0.03em] ${accent ? "text-plum" : "text-foreground"}`}>
              {loading ? "…" : value ?? 0}
            </strong>
            <span className="mt-1 block text-[13px] text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>

      <section className={`${cardClass} mt-4 p-6`}>
        <h2 className="font-display text-[16px] font-semibold tracking-[-0.02em] text-foreground">How it works</h2>
        <ol className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {steps.map(({ icon: Icon, label, detail }, i) => (
            <li key={label} className="rounded-xl border border-line bg-soft p-4">
              <div className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-surface text-plum">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Step {i + 1}</span>
              </div>
              <p className="mt-3 text-[14px] font-semibold text-foreground">{label}</p>
              <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{detail}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};

export default ReferralsPage;
